import { FC, useMemo, useRef, useState } from "react";
// 组件
import { Table, Form, } from 'antd';
import EditSubTable from "./subpage/editSubTable";
import MainTableCell from "./subpage/mainTableCell";
// 方法
import classNames from "classnames";
import useDynamicSubTableHook, { IDynamicSubTablePayload } from "./hook/useDynamicSubTableHook";
import useColumnsHook from "./hook/useColumnsHook";
import DynamicSubTableContext from './context/dynamicSubTableContext';
// 常量
import { TableProps } from 'antd/lib/table';
import { CHILDREN_COLUMNS_KEY, CHILDREN_DATA_KEY, EditDynamicSubTableProps, MAIN_FORM_KEY } from "./interface";
import './style.less';
import { filterDeleteData, formatDataSource, getRowDataSource } from "./util";

/** 可编辑主子表动态子表列 */
const EditDynamicSubTable: FC<EditDynamicSubTableProps> = props => {
  const { className, style, defaultExpandAllRows, actionRef, virtual, onValuesChange, } = props;
  const tableRef = useRef<HTMLDivElement>(null);
  const flagIndex = useRef<number>(0);
  const [form] = Form.useForm();
  const { dataSource, expandedRowKeys, dispatchDynamicSubTable, } = useDynamicSubTableHook({flagIndex, dataSource: props.dataSource, form, defaultExpandAllRows });
  const { columns } = useColumnsHook({props});
  /** 字段值更新时触发回调事件 */
  const handleValuesChange = (changedValues: any, allValues: any) => {
    const [mainKey, changeFormItem] = Object.entries(changedValues)[0];
    const [childrenKey, changeItem] = Object.entries(changeFormItem as any)[0];
    const dispatchData: IDynamicSubTablePayload = {
      mainKey,
      childrenKey,
    };
    if (childrenKey === MAIN_FORM_KEY) {
      dispatchData.changeMainItem = changeItem;
    } else {
      dispatchData.changeChildrenItem = changeItem;
    }
    const changeName = Object.keys(changeItem as any)[0];
    onValuesChange?.(changeName, changeItem, mainKey, childrenKey, form, getRowDataSource(mainKey, childrenKey, allValues[mainKey][childrenKey], dataSource));
    dispatchDynamicSubTable('change', dispatchData);
  };
  /** 提交 */
  const handleSubmit = async() => {
    try {
      await form.validateFields();
      return formatDataSource(dataSource, false, true);
    } catch (errorInfo: any) {
      return false;
    }
  };
  // 获取当前可编辑表的值
  const handleDataSource = (filterDeleteData?: boolean) => {
    return formatDataSource(dataSource, filterDeleteData);
  };
  /** 清空 */
  const handleEmpty = () => {
    dispatchDynamicSubTable('empty');
  };
  /** 展开功能的配置 */
  const expandable: TableProps['expandable'] = useMemo(() => {
    return {
      childrenColumnName: '@empty@',
      columnWidth: 50,
      expandedRowRender: (record: any, index: number) => {
        return (
          <EditSubTable
            dataSource={record[CHILDREN_DATA_KEY]}
            childrenColumns={record[CHILDREN_COLUMNS_KEY]}
            mainKey={record.key}
          />
        );
      },
      expandedRowKeys: expandedRowKeys,
      onExpandedRowsChange: (expandedRows: any) => {
        dispatchDynamicSubTable('changeExpandedRowKeys', {expandedRowKeys: expandedRows});
      },
    };
  }, [expandedRowKeys]);
  /** 可编辑表格展示的数据 */
  const tableDataSource = useMemo(() => {
    const newDataSource = filterDeleteData(dataSource);
    return newDataSource;
  }, [dataSource]);
  if (actionRef) {
    actionRef.current = {
      submit: handleSubmit,
      getDataSource: handleDataSource,
      empty: handleEmpty,
    };
  }

  return (
    <DynamicSubTableContext.Provider
      value={{
        form,
        dataSource,
        expandedRowKeys,
      }}
    >
      <div
        ref={tableRef}
        className={classNames('dynamic-sub-table', className)}
        style={style}
      >
        <Form
          component={false}
          form={form}
          name='dynamic-sub-table-form'
          onValuesChange={handleValuesChange}
        >
          <Table
            dataSource={tableDataSource}
            columns={columns}
            expandable={expandable}
            size="small"
            components={{
              body: {
                cell: MainTableCell,
              },
            }}
            pagination={false}
            virtual={virtual}
            scroll={{
              x: 'max-content',
              y: 500,
            }}
          />
        </Form>
      </div>
    </DynamicSubTableContext.Provider>
  );
};

EditDynamicSubTable.defaultProps = {
  defaultExpandAllRows: true,
  virtual: true,
};

export default EditDynamicSubTable;
